import { useState } from "react";
import { Navigate, useLocation } from "react-router";
import { Button, ConsentPage, Logo } from "@omnabase/ui";
import type { Me } from "@omnabase/shared";
import { CONSENT_ROUTE, consentBody, consentRequestOf, signedQueryOf, type ConsentRequest } from "../lib/oauthConsent";
import { CONNECTOR_CHECKLIST, connectedForLine } from "../lib/connections";
import { signOutHere } from "../lib/push";

/** M9 round two: the consent page (brief task 2, B2). A plain page outside the workspace shell, reached from Claude's sign-in with a signed
 *  query: the workspace from the signed connector address (no choice), the host of the app's published description and the host the sign-in
 *  goes back to, in large type, never the app's self-chosen name. Allow or Cancel posts the signed query to `/api/auth/oauth2/consent` (same
 *  origin, used once, the server checks the membership again) and follows the address it answers; the API serves this page with
 *  `frame-ancestors 'none'`. The sidebar and consent round (2026-10-24, the founder's mockup): the kit's ConsentPage, "Let <app> into
 *  <workspace>?" with the shared checklist, the amber strip (where the sign-in goes back to when it is another host; for an app OmnaBase does
 *  not know the extra line and the member's own tick, which the server needs before it allows), and "Not you?", which signs this browser out
 *  and keeps the signed request for the right person's sign-in. */
export function ConsentScreen({ me }: { me: Me | null }) {
  const { search } = useLocation();
  const query = signedQueryOf(search);
  if (!me && query) return <Navigate to={`/signin?${query}`} replace />;
  const req = query ? consentRequestOf(query) : null;
  const workspace = req?.slug ? me?.workspaces.find((w) => w.slug === req.slug) ?? null : null;
  return (
    <div className="ob-signin">
      <aside className="ob-signin__aside" aria-hidden="true"><div className="ob-signin__aside-inner"><span className="ob-signin__logo"><Logo /></span><p className="ob-signin__claim">Connect your AI</p><p className="ob-signin__line">Your own AI reads your queue as you, with your rights.</p></div></aside>
      <main className="ob-signin__main" aria-label="Allow access">
        <div className="ob-signin__box ob-signin__box--consent">
          {!req || !query ? <Invalid /> : !workspace ? <NotMember me={me} req={req} query={query} /> : <Decision me={me!} req={req} query={query} workspace={workspace} />}
        </div>
        <p className="ob-signin__legal">OmnaBase</p>
      </main>
    </div>
  );
}

/** the member's answer posted with the signed query; the browser follows the address the server answers */
function useAnswer(query: string, confirmUnknown: boolean) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const answer = async (accept: boolean) => {
    if (busy) return;
    setBusy(true); setProblem(null);
    const r = await fetch(CONSENT_ROUTE, { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(consentBody({ accept, query, confirmUnknown })) });
    const body = await r.json().catch(() => null) as { url?: string; error_description?: string; message?: string } | null;
    if (r.ok && body?.url) { window.location.assign(body.url); return; }
    setBusy(false);
    setProblem(body?.error_description?.replace(/^\w+: /, "") ?? body?.message ?? "This sign-in could not be completed. Start connecting again from your AI app.");
  };
  return { answer, problem, busy };
}

function Invalid() {
  return <div className="ob-signin__form"><h1 className="ob-signin__title">This sign-in link is not valid</h1><p className="ob-signin__intro">Start connecting again from your AI app.</p></div>;
}

function NotMember({ me, req, query }: { me: Me | null; req: ConsentRequest; query: string }) {
  const { answer, problem, busy } = useAnswer(query, false);
  return (
    <div className="ob-signin__form">
      <h1 className="ob-signin__title">You are not a member of this workspace</h1>
      <p className="ob-signin__intro">{me?.user.email} cannot connect an AI to <strong>{req.slug ?? "this address"}</strong>.</p>
      {problem && <p className="ob-signin__problem" role="alert">{problem}</p>}
      <Button className="ob-signin__btn" disabled={busy} onClick={() => void answer(false)}>Cancel</Button>
    </div>
  );
}

const STRIP = "Only allow if you started this yourself just now.";
const UNKNOWN = "OmnaBase does not know this app.";
/** the checklist as the page's lists: the can lines as "It can", the never line under the hairline */
const CAN = CONNECTOR_CHECKLIST.filter((c) => c.ok).map((c) => ({ text: c.text, note: c.note }));
const NEVER = CONNECTOR_CHECKLIST.find((c) => !c.ok)?.text;

/** the decision: who asks (the host of its published description, or "an AI app"), the checklist, the strip and the two answers */
function Decision({ me, req, query, workspace }: { me: Me; req: ConsentRequest; query: string; workspace: Me["workspaces"][number] }) {
  const unknownApp = req.returnKind === "unknown-https";
  const [confirmed, setConfirmed] = useState(false);
  const { answer, problem, busy } = useAnswer(query, unknownApp && confirmed);
  const app = req.clientHost ?? "an AI app";
  const backTo = req.returnHost && req.returnHost !== req.clientHost ? req.returnHost : null; // the phishing check: where the sign-in really goes
  const notYou = async () => { await signOutHere(); window.location.assign(`/signin?${query}`); };
  return (
    <ConsentPage
      asker={{ name: req.clientHost ?? "An AI app", host: !!req.clientHost, icon: "bot" }}
      workspace={{ name: workspace.name, mark: workspace.mark }}
      title={`Let ${app} into ${workspace.name}?`}
      can={CAN} never={NEVER}
      strip={{
        warning: STRIP, more: unknownApp ? [UNKNOWN] : [],
        before: backTo && <p className="ob-consent__strip-line">It sends your sign-in back to <span className="ob-consent__host">{backTo}</span></p>,
        after: unknownApp && <label className="ob-consent__confirm"><input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />I set this app up myself and want it to work in this workspace as me</label>,
      }}
      problem={problem}
      actions={{ primary: "Allow", onPrimary: () => void answer(true), disabled: busy || (unknownApp && !confirmed), onCancel: () => void answer(false), cancelDisabled: busy }}
      signedInAs={{ email: me.user.email, onNotYou: () => void notYou() }}
      footnote={connectedForLine(req.offline)}
    />
  );
}
